import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography, Box, Button, Paper } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useEncryptedSearchParams } from "../hooks/useEncryptedSearchParams";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import moment from "moment";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2/dist/js/select2.full.min.js";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import { complaintAPI, userAPI } from "../services/api";
import { approverDashboardAPI } from "../services/approverDashboardApi";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType, isDosAdminUser, getUser } from "../utils/auth";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import {
  saveBcmServiceRequestListState,
  readBcmServiceRequestListState,
  clearBcmServiceRequestListState,
  readBcmServiceRequestListRestoreSnapshot,
  markBcmServiceRequestListRestored,
} from "../utils/bcmServiceRequestListState";
import { markComplaintsListRestored } from "../utils/complaintsListState";

/** Read BV drill-down CSV directly from URL (source of truth for encrypted ?q= links). */
function parseBusinessVerticalCsvFromSearchParams(urlSearchParams) {
  if (!urlSearchParams) return null;
  const bvParam = urlSearchParams.get("bv");
  if (!bvParam) return null;
  const codes = decodeURIComponent(bvParam)
    .split(",")
    .map((s) => String(s || "").trim())
    .filter(Boolean);
  return codes.length > 0 ? codes.join(",") : null;
}

function parseBusinessVerticalNameFromSearchParams(urlSearchParams) {
  if (!urlSearchParams) return null;
  const name = urlSearchParams.get("bvName");
  return name ? decodeURIComponent(name).trim() : null;
}

/** Expand CATGCODE list + display names for API + client-side BV filter. */
function buildBusinessVerticalMatchContext(bvCsv, bvName, bvOptions = []) {
  const codes = new Set();
  String(bvCsv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((c) => codes.add(c));

  const names = new Set();
  const nameKey = bvName ? String(bvName).trim().toLowerCase() : "";
  if (nameKey) names.add(nameKey);

  (Array.isArray(bvOptions) ? bvOptions : []).forEach((o) => {
    const id = String(o?.id ?? "").trim();
    const text = String(o?.text ?? "").trim();
    const textKey = text.toLowerCase();
    if (!id && !text) return;
    if (id && codes.has(id) && textKey) names.add(textKey);
    if (nameKey && textKey === nameKey) {
      if (id) codes.add(id);
      names.add(textKey);
    }
  });

  // Name-only drill-down (or code not yet resolved): keep a token for API + client filter.
  if (codes.size === 0 && bvName) {
    codes.add(String(bvName).trim());
  }

  return {
    codesCsv: codes.size > 0 ? [...codes].join(",") : null,
    names: [...names],
    exactNameMatch: Boolean(nameKey),
  };
}

/** Dropdown label: show name only (strip " (Territory)" / " (Role)" suffix). */
function displayNameOnly(label) {
  const s = String(label ?? "").trim();
  const idx = s.indexOf(" (");
  return idx > 0 ? s.slice(0, idx).trim() : s;
}

/** Client-side BV filter when SP has not yet been updated on the server. */
function filterItemsByBusinessVertical(items, matchContext) {
  if (!matchContext?.names?.length && !matchContext?.codesCsv) return items;

  const names = (matchContext.names || []).map((n) => String(n).trim().toLowerCase()).filter(Boolean);
  const codes = new Set(
    String(matchContext.codesCsv || "")
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
  );
  const exactNameOnly = Boolean(matchContext.exactNameMatch && names.length > 0);

  return (items || []).filter((item) => {
    const raw = String(item.productCategory || item.businessVertical || "").trim();
    if (!raw) return false;
    const parts = raw
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);

    return parts.some((part) => {
      const partLower = part.toLowerCase();
      const partUpper = part.toUpperCase();
      if (codes.size > 0 && codes.has(partUpper)) return true;
      if (names.length === 0) return false;
      if (exactNameOnly) return names.some((n) => partLower === n);
      return names.some(
        (n) => partLower === n || partLower.includes(n) || n.includes(partLower)
      );
    });
  });
}

function escapeHtmlAttr(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

/** SR NO link — avoid href="#" (breaks when URL ends with # from encrypted drill-down). */
function buildSrLinkHtml(row, label) {
  const parentSrCode = String(row?.srCode || row?.id || "").trim();
  const childSrCode = String(row?.srNumber || row?.srNo || "").trim();
  const text =
    label != null && String(label).trim() !== ""
      ? String(label)
      : childSrCode || parentSrCode;
  if (!text) return "-";
  return `<a href="javascript:void(0)" role="button" class="sr-link" style="color:#007bff;text-decoration:underline;cursor:pointer;" data-parent-sr-code="${escapeHtmlAttr(parentSrCode)}" data-child-sr-code="${escapeHtmlAttr(childSrCode)}">${escapeHtmlAttr(text)}</a>`;
}

/** Approver-dashboard pie/chart tokens → SRSTATUSMASTER option ids for CustomMultiSelect. */
const DASHBOARD_STATUS_TOKENS = new Set(["2", "3", "4", "5", "7"]);

const DASHBOARD_STATUS_NAME_MATCHERS = {
  "2": (t) => t === "pending" || t.includes("pending"),
  "3": (t) => t === "approved" || (t.includes("approved") && !t.includes("payment")),
  "4": (t) => t === "rejected" || t.includes("reject"),
  "5": (t) => t === "closed" || t.includes("closed"),
  "7": (t) =>
    t === "wip" ||
    t.includes("wip") ||
    t.includes("payment pending") ||
    t.includes("paymentpending"),
};

function isDashboardStatusToken(value) {
  return DASHBOARD_STATUS_TOKENS.has(String(value ?? "").trim());
}

function mapDashboardTokensToStatusMasterIds(tokens, statusOptions = []) {
  const ids = [];
  (Array.isArray(tokens) ? tokens : []).forEach((token) => {
    const key = String(token ?? "").trim();
    const matchFn = DASHBOARD_STATUS_NAME_MATCHERS[key];
    if (!matchFn) return;
    const match = (Array.isArray(statusOptions) ? statusOptions : []).find((o) => {
      const text = (o.text || "").toString().trim().toLowerCase();
      return matchFn(text);
    });
    if (match?.id) ids.push(String(match.id));
  });
  return [...new Set(ids)];
}

/** Map displayed row status labels → SRSTATUSMASTER option ids for checkbox UI. */
function mapStatusNamesToMasterIds(statusNames, statusOptions = []) {
  const ids = new Set();
  (Array.isArray(statusNames) ? statusNames : []).forEach((name) => {
    const key = String(name ?? "").trim().toLowerCase();
    if (!key) return;
    const match = (Array.isArray(statusOptions) ? statusOptions : []).find((o) => {
      const id = String(o?.id ?? "").trim().toLowerCase();
      const text = String(o?.text ?? "").trim().toLowerCase();
      return id === key || text === key;
    });
    if (match?.id) ids.add(String(match.id));
  });
  return [...ids];
}

function getAllStatusMasterIds(statusOptions = []) {
  return (Array.isArray(statusOptions) ? statusOptions : [])
    .map((o) => String(o?.id ?? "").trim())
    .filter(Boolean);
}

function statusIdSetsEqual(a, b) {
  const left = (Array.isArray(a) ? a : []).map((v) => String(v ?? "").trim()).filter(Boolean).sort();
  const right = (Array.isArray(b) ? b : []).map((v) => String(v ?? "").trim()).filter(Boolean).sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
}

/** Chart drill-down scope: pending + activity done by approver (matches dashboard bar totals). */
const CHART_SCOPE_STATUS_TOKENS = ["2", "3", "4", "5"];

/**
 * Module-level single-flight for approver list HTTP.
 * React Strict Mode / rapid rebinds must share one in-flight request for the same key
 * (overlapping SP calls starved the pool and tripped the 20s overlay timeout).
 */
const approverListFetchFlight = {
  key: null,
  promise: null,
};

function fetchApproverListShared(fetchKey, exec) {
  if (approverListFetchFlight.key === fetchKey && approverListFetchFlight.promise) {
    return approverListFetchFlight.promise;
  }
  const promise = Promise.resolve()
    .then(exec)
    .finally(() => {
      if (approverListFetchFlight.promise === promise) {
        approverListFetchFlight.key = null;
        approverListFetchFlight.promise = null;
      }
    });
  approverListFetchFlight.key = fetchKey;
  approverListFetchFlight.promise = promise;
  return promise;
}

function hasBcmDrilldownSearchParams(urlSearchParams) {
  if (!urlSearchParams) return false;
  return (
    Boolean(urlSearchParams.get("status")) ||
    Boolean(urlSearchParams.get("bv")) ||
    Boolean(urlSearchParams.get("bvName")) ||
    Boolean(urlSearchParams.get("category")) ||
    Boolean(urlSearchParams.get("valueSlab")) ||
    Boolean(urlSearchParams.get("queryRaised")) ||
    Boolean(urlSearchParams.get("queryToRespond")) ||
    Boolean(urlSearchParams.get("specialCase")) ||
    Boolean(urlSearchParams.get("slaBreached"))
  );
}

/** Sync parse of dashboard→list URL so first paint already has filters (no bind-effect race). */
function parseDrilldownAppliedFilters(urlSearchParams, userIdCode, defaultFrom, defaultTo) {
  const parseCsv = (v) =>
    String(v || "")
      .split(",")
      .map((s) => decodeURIComponent(String(s || "")).trim())
      .filter(Boolean);

  const statusParam = urlSearchParams?.get("status");
  const categoryParam = urlSearchParams?.get("category");
  const valueSlabParam = urlSearchParams?.get("valueSlab");
  const fromDateParam = urlSearchParams?.get("fromDate");
  const toDateParam = urlSearchParams?.get("toDate");
  const queryRaisedParam = urlSearchParams?.get("queryRaised");
  const queryToRespondParam = urlSearchParams?.get("queryToRespond");
  const specialCaseParam = urlSearchParams?.get("specialCase");
  const slaBreachedParam = urlSearchParams?.get("slaBreached");
  const onlyQueriesRaisedByMe =
    queryRaisedParam === "true" ||
    queryRaisedParam === "1" ||
    String(queryRaisedParam || "").toLowerCase() === "true";
  const onlyQueriesToRespondByMe =
    queryToRespondParam === "true" ||
    queryToRespondParam === "1" ||
    String(queryToRespondParam || "").toLowerCase() === "true";
  const onlySpecialCase =
    specialCaseParam === "true" ||
    specialCaseParam === "1" ||
    String(specialCaseParam || "").toLowerCase() === "true";
  const onlySlaBreached =
    slaBreachedParam === "true" ||
    slaBreachedParam === "1" ||
    String(slaBreachedParam || "").toLowerCase() === "true";

  let statusCode = null;
  let statusCodesCsv = null;
  let urlDashboardStatusToken = null;
  let chartScopeStatusCsv = null;
  let syncStatusFilterFromResults = false;

  if (statusParam) {
    const parts = parseCsv(statusParam);
    if (parts.length > 0) {
      const urlStatus = parts[0];
      const isDashboardNumeric =
        /^\d+$/.test(urlStatus) && ["2", "3", "4", "5", "7"].includes(urlStatus);
      const allDashboardNumeric =
        parts.length > 1 &&
        parts.every((p) => /^\d+$/.test(p) && ["2", "3", "4", "5", "7"].includes(p));
      if (allDashboardNumeric) {
        chartScopeStatusCsv = parts.join(",");
        syncStatusFilterFromResults = true;
      } else if (isDashboardNumeric) {
        urlDashboardStatusToken = urlStatus;
        statusCode = urlStatus;
      } else if (parts.length === 1) {
        statusCode = urlStatus;
      } else {
        statusCodesCsv = parts.join(",");
      }
    }
  } else if (!onlyQueriesRaisedByMe && !onlyQueriesToRespondByMe && !onlySpecialCase && !onlySlaBreached) {
    const isChartAggregate =
      Boolean(categoryParam) ||
      Boolean(parseBusinessVerticalCsvFromSearchParams(urlSearchParams)) ||
      Boolean(parseBusinessVerticalNameFromSearchParams(urlSearchParams)) ||
      Boolean(valueSlabParam);
    if (isChartAggregate) {
      chartScopeStatusCsv = CHART_SCOPE_STATUS_TOKENS.join(",");
      syncStatusFilterFromResults = true;
    }
  }

  let businessVerticalCodesCsv = parseBusinessVerticalCsvFromSearchParams(urlSearchParams);
  const businessVerticalName = parseBusinessVerticalNameFromSearchParams(urlSearchParams);
  if (!businessVerticalCodesCsv && businessVerticalName) {
    businessVerticalCodesCsv = businessVerticalName;
  }

  const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
  const fromRaw = fromDateParam ? decodeURIComponent(fromDateParam) : "";
  const toRaw = toDateParam ? decodeURIComponent(toDateParam) : "";
  const fromIso = isIso(fromRaw) ? fromRaw : defaultFrom;
  const toIso = isIso(toRaw) ? toRaw : defaultTo;
  const valueSlabDecoded = valueSlabParam ? decodeURIComponent(valueSlabParam).trim() : "";

  return {
    appliedFilters: {
      createdBy: userIdCode || null,
      statusCode,
      statusCodesCsv,
      businessVerticalCodesCsv: businessVerticalCodesCsv || null,
      valueSlab: valueSlabDecoded || null,
      fromDate: fromIso,
      toDate: toIso,
      raisedByUserIdCode: null,
      searchText: null,
      onlyQueriesRaisedByMe,
      onlyQueriesToRespondByMe,
      onlySpecialCase,
      onlySlaBreached,
    },
    urlDashboardStatusToken,
    chartScopeStatusCsv,
    syncStatusFilterFromResults,
    drilldownBvCsv: businessVerticalCodesCsv || null,
    drilldownBvName: businessVerticalName || businessVerticalCodesCsv || null,
    fromIso,
    toIso,
    categoryParam: categoryParam ? decodeURIComponent(categoryParam) : "",
  };
}

const BCMServiceRequestListPage = ({ urlSearchParams }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchKey = urlSearchParams.toString();
  const srTableRef = useRef(null);
  const srTableWrapperRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  /** Persists BV from encrypted URL so the first API call cannot miss it during batched setState. */
  const drilldownBvCsvRef = useRef(null);
  const drilldownBvNameRef = useRef(null);
  /** Chart drill-down: after options load, check all status options (not only statuses in result rows). */
  const syncStatusFilterFromResultsRef = useRef(false);
  /** Keep dashboard status tokens (2,3,4,5) for API until user clicks Filter — avoids losing approved rows. */
  const chartScopeStatusCsvRef = useRef(null);
  /** URL status token (e.g. "2") — always preferred for list API over mapped master ids. */
  const urlDashboardStatusTokenRef = useRef(null);
  /** Skip redundant reload when only syncing checkbox UI from loaded rows. */
  const suppressLoadForStatusUiSyncRef = useRef(false);
  const restoreAppliedRef = useRef(false);
  const statusFilterRef = useRef([]);
  const productVerticalFilterRef = useRef([]);
  const appliedFiltersRef = useRef(null);
  const startDateRef = useRef("");
  const endDateRef = useRef("");
  const raisedByFilterRef = useRef("");
  const srInvoiceFilterRef = useRef("");
  const complaintTypeFilterRef = useRef("");
  const categoryFilterRef = useRef("");
  const listLocationRef = useRef({ pathname: "/bcm-service-requests", search: "" });
  /** Latest master/options for load() — avoids re-fetching when dropdown data arrives. */
  const userListRef = useRef([]);
  const bvOptionsRef = useRef([]);
  const urlSearchParamsRef = useRef(urlSearchParams);
  /** Skip back-to-back identical list API calls (e.g. React Strict Mode / overlapping effects). */
  const lastCompletedListFetchKeyRef = useRef("");
  /** Prevent concurrent duplicate list loads for the same fetchKey. */
  const inFlightListFetchKeyRef = useRef(null);
  /** Monotonic id so only the latest list request may clear the loading overlay. */
  const listLoadSeqRef = useRef(0);
  /** Last URL search key successfully bound into appliedFilters. */
  const lastBoundUrlSearchKeyRef = useRef("");
  const { sidebarOpen } = useSidebar();

  const getCurrentFinancialYearRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 3 = Apr
    const fyStartYear = month < 3 ? year - 1 : year;
    const fyEndYear = month < 3 ? year : year + 1;
    const start = `${fyStartYear}-04-01`;
    const end = `${fyEndYear}-03-31`;
    const fmt = (y, m, d) => {
      const dd = String(d).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      return `${dd}-${mm}-${y}`;
    };
    const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
    return { start, end, displayText };
  };

  const defaultFY = getCurrentFinancialYearRange();
  // Dashboard chart/card drill-down must not hydrate a stale "return from SR detail" snapshot.
  const mountHasDrilldown = hasBcmDrilldownSearchParams(urlSearchParams);
  const savedRestoreSnapshot = mountHasDrilldown
    ? null
    : readBcmServiceRequestListRestoreSnapshot();
  const restoredStartDate = savedRestoreSnapshot?.startDate || defaultFY.start;
  const restoredEndDate = savedRestoreSnapshot?.endDate || defaultFY.end;
  const restoredDateRangeText =
    savedRestoreSnapshot?.startDate && savedRestoreSnapshot?.endDate
      ? `${moment(savedRestoreSnapshot.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
          savedRestoreSnapshot.endDate,
          "YYYY-MM-DD"
        ).format("DD-MM-YYYY")}`
      : defaultFY.displayText;

  // Apply URL drill-down BEFORE any useState so first paint already has correct filters.
  const initialDrilldown = (() => {
    if (!mountHasDrilldown) return null;
    const u = getUser();
    const uid =
      (u?.userIdCode || u?.UserIdCode || u?.USERIDCODE || "").toString().trim() || null;
    if (!uid) return null;
    return parseDrilldownAppliedFilters(
      urlSearchParams,
      uid,
      defaultFY.start,
      defaultFY.end
    );
  })();

  // Seed refs once before any load effect (render-phase is OK for initial mount only).
  if (initialDrilldown && !lastBoundUrlSearchKeyRef.current) {
    lastBoundUrlSearchKeyRef.current = urlSearchKey;
    urlDashboardStatusTokenRef.current = initialDrilldown.urlDashboardStatusToken;
    chartScopeStatusCsvRef.current = initialDrilldown.chartScopeStatusCsv;
    syncStatusFilterFromResultsRef.current = initialDrilldown.syncStatusFilterFromResults;
    drilldownBvCsvRef.current = initialDrilldown.drilldownBvCsv;
    drilldownBvNameRef.current = initialDrilldown.drilldownBvName;
    if (initialDrilldown.categoryParam) {
      complaintTypeFilterRef.current = initialDrilldown.categoryParam;
      categoryFilterRef.current = initialDrilldown.categoryParam;
    }
  }

  // Filter states
  const [statusFilter, setStatusFilter] = useState(() =>
    Array.isArray(savedRestoreSnapshot?.statusFilter) ? [...savedRestoreSnapshot.statusFilter] : []
  );
  const [categoryFilter, setCategoryFilter] = useState(
    () =>
      String(
        initialDrilldown?.categoryParam ||
          savedRestoreSnapshot?.categoryFilter ||
          ""
      )
  );
  const [complaintTypeFilter, setComplaintTypeFilter] = useState(
    () =>
      String(
        initialDrilldown?.categoryParam ||
          savedRestoreSnapshot?.complaintTypeFilter ||
          ""
      )
  );
  const [productVerticalFilter, setProductVerticalFilter] = useState(() => {
    if (initialDrilldown?.drilldownBvCsv) {
      return initialDrilldown.drilldownBvCsv.split(",").filter(Boolean);
    }
    return Array.isArray(savedRestoreSnapshot?.productVerticalFilter)
      ? [...savedRestoreSnapshot.productVerticalFilter]
      : [];
  });
  const [raisedByFilter, setRaisedByFilter] = useState(
    () => String(savedRestoreSnapshot?.raisedByFilter || "")
  );
  const [srInvoiceFilter, setSrInvoiceFilter] = useState(
    () => String(savedRestoreSnapshot?.srInvoiceFilter || "")
  );
  const [startDate, setStartDate] = useState(
    () => initialDrilldown?.fromIso || restoredStartDate
  );
  const [endDate, setEndDate] = useState(
    () => initialDrilldown?.toIso || restoredEndDate
  );
  const [dateRangeText, setDateRangeText] = useState(() => {
    if (initialDrilldown?.fromIso && initialDrilldown?.toIso) {
      return `${moment(initialDrilldown.fromIso, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
        initialDrilldown.toIso,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY")}`;
    }
    return restoredDateRangeText;
  });
  // Date range is aligned with /csrp/approver-dashboard (default: current financial year)
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [listFetchDone, setListFetchDone] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);

  /** After options load, map /?status=2|3|4|5|7 (dashboard tokens) to SRSTATUSMASTER ids for CustomMultiSelect. */
  const [pendingDashboardStatusTokens, setPendingDashboardStatusTokens] = useState(() =>
    initialDrilldown?.urlDashboardStatusToken
      ? [initialDrilldown.urlDashboardStatusToken]
      : []
  );

  const [currentUserIdCode, setCurrentUserIdCode] = useState(() => {
    const u = getUser();
    return (
      (u?.userIdCode || u?.UserIdCode || u?.USERIDCODE || "").toString().trim() ||
      null
    );
  });

  // Applied filters for server-side API (sp_GetServiceRequestList); set when user clicks Filter
  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (initialDrilldown?.appliedFilters) {
      return initialDrilldown.appliedFilters;
    }
    if (savedRestoreSnapshot?.appliedFilters) {
      return {
        ...savedRestoreSnapshot.appliedFilters,
        createdBy: null,
      };
    }
    return {
      createdBy: null,
      statusCode: null,
      statusCodesCsv: null,
      businessVerticalCodesCsv: null,
      valueSlab: null,
      fromDate: defaultFY.start,
      toDate: defaultFY.end,
      raisedByUserIdCode: null,
      searchText: null,
      onlyQueriesRaisedByMe: false,
      onlyQueriesToRespondByMe: false,
      onlySpecialCase: false,
      onlySlaBreached: false,
    };
  });
  // Bound immediately when URL has drill-down — first load effect can run without a blank race.
  const [urlFiltersBound, setUrlFiltersBound] = useState(
    Boolean(initialDrilldown) || Boolean(savedRestoreSnapshot)
  );
  const urlFiltersBoundRef = useRef(Boolean(initialDrilldown) || Boolean(savedRestoreSnapshot));
  const prevUserIdForClearRef = useRef(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Master data for filter dropdowns (from APIs) — same source as /csrp/approver-dashboard
  const [statusOptionsFromApi, setStatusOptionsFromApi] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [userList, setUserList] = useState([]); // from user master

  // Helper functions reused from Dashboard presets
  const getFinancialYearStart = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < 3) {
      // Jan-Mar: FY starts previous year April
      return new Date(year - 1, 3, 1);
    }
    return new Date(year, 3, 1);
  };

  const getQuarterStart = (date) => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  const getUniqueValues = (values = []) =>
    Array.from(
      new Set(values.filter((item) => item !== undefined && item !== null && item !== ""))
    );

  // Status options: from sp_GetSRStatusList (Greenlam.SRSTATUSMASTER), populated in useEffect
  const statusOptions = statusOptionsFromApi;

  // Raised By / Territory options: from user master (userList), populated in useEffect
  const raisedByOptionsFromMaster = (userList || []).map((u) => {
    const name =
      u.fullName ||
      u.FullName ||
      u.displayName ||
      u.DisplayName ||
      u.userName ||
      u.UserName ||
      u.userIdCode ||
      u.UserIdCode ||
      "";
    return {
      id: u.userIdCode || u.UserIdCode || "",
      text: displayNameOnly(name),
    };
  });

  const complaintTypeOptions = getUniqueValues(
    allData.map(
      (item) =>
        item.complaintTypeName || item.ComplaintTypeName || item.complaintCategory || ""
    )
  );

  // Product/Vertical and Raised By options come from master APIs (bvOptions, userList).

  const raisedByOptionsFromData = getUniqueValues(
    allData.map(
      (item) =>
        item.createdByName ||
        item.CreatedByName ||
        item.raisedBy ||
        item.RaisedBy ||
        item.territory ||
        item.Territory ||
        ""
    )
  );
  const raisedByOptions =
    raisedByOptionsFromMaster.length > 0
      ? raisedByOptionsFromMaster
      : raisedByOptionsFromData.map((text) => ({
          id: text,
          text: displayNameOnly(text),
        }));

  const srInvoiceSuggestions = getUniqueValues(
    allData
      .flatMap((item) => {
        const sr =
          item.srNumber ||
          item.SRNumber ||
          item.complaintNumber ||
          item.ComplaintNumber ||
          item.ComplaintNo ||
          item.complaintNo ||
          item.Id ||
          item.id ||
          "";
        const invoice =
          item.invoiceNumber ||
          item.InvoiceNumber ||
          item.invoiceNo ||
          item.InvoiceNo ||
          "";
        return [sr, invoice]
          .map((val) => (val == null ? "" : String(val).trim()))
          .filter((val) => val);
      })
  );

  // Load master data for filter dropdowns: SR status, business vertical, users
  useEffect(() => {
    let mounted = true;

    const loadMasters = async () => {
      if (!currentUserIdCode) return;
      try {
        // Do NOT Promise.all with getSeByTerritory — for UT0000024 (DOS Admin) that call
        // can be huge and was blocking status/BV options (and BV drill-down list load).
        const [statusRes, bvRes] = await Promise.all([
          approverDashboardAPI.getSRStatuses(true),
          approverDashboardAPI.getProductVerticalOptions({
            isActive: true,
            pageNumber: 1,
            pageSize: 500,
          }),
        ]);

        if (!mounted) return;

        const statusOk = statusRes?.success === true || statusRes?.Success === true;
        const statusList = statusRes?.data ?? statusRes?.Data;
        if (statusOk && Array.isArray(statusList)) {
          setStatusOptionsFromApi(
            statusList.map((s) => ({
              id: s.srStatusCode || s.sRStatusCode || s.SRStatusCode || s.code || "",
              text: s.name || s.Name || s.NAME || "",
            })).filter((o) => o.id)
          );
        }

        const bvPayload = bvRes?.data ?? bvRes?.Data;
        const bvList = bvPayload?.categories ?? bvPayload?.Categories ?? [];
        if (Array.isArray(bvList)) {
          setBvOptions(
            bvList
              .map((c) => ({
                id: c.catgCode || c.CatgCode || "",
                text: c.catgName || c.CatgName || c.catgCode || c.CatgCode || "",
              }))
              .filter((o) => o.id && o.text)
          );
        }

        // Raised-by dropdown: never hydrate All-territory dumps (800+ users) into Select2 —
        // that freezes /bcm-service-requests for DOS Admin / Country Head.
        const loginUser = getUser();
        const territoryCode = String(
          loginUser?.TerritoryCode || loginUser?.territoryCode || ""
        ).trim();
        const territoryName = String(
          loginUser?.TerritoryName || loginUser?.territoryName || ""
        ).trim();
        const isAllTerritory =
          territoryCode === "0000" ||
          /^all$/i.test(territoryCode) ||
          /^all$/i.test(territoryName);
        const skipSeTerritory =
          isDosAdminUser() ||
          hasUserType("UT0000024") ||
          hasUserType("UT0000009") ||
          isAllTerritory;
        if (!skipSeTerritory) {
          userAPI
            .getSeByTerritory({ approverUserIdCode: currentUserIdCode, status: "active" })
            .then((usersRes) => {
              if (!mounted) return;
              const users = usersRes?.data ?? (Array.isArray(usersRes) ? usersRes : []);
              if (!Array.isArray(users) || users.length === 0) return;
              // Hard safety cap — even if role/territory detection misses, do not freeze the UI.
              if (users.length > 100) {
                console.warn(
                  "[BCM SR List] Skipping oversized SE-by-territory list:",
                  users.length
                );
                return;
              }
              setUserList(users);
            })
            .catch((err) => {
              console.error("Failed to load Raised By users", err);
            });
        }
      } catch (err) {
        console.error("Failed to load filter masters", err);
      }
    };

    loadMasters();
    return () => { mounted = false; };
  }, [currentUserIdCode]);

  // Resolve current user's UserIdCode once on mount (prevents accidental broad queries)
  useEffect(() => {
    const u = getUser();
    const id =
      (u?.userIdCode || u?.UserIdCode || u?.USERIDCODE || "").toString().trim();
    if (!id) return;
    setCurrentUserIdCode((prev) => (prev === id ? prev : id));
  }, []);

  useEffect(() => {
    statusFilterRef.current = Array.isArray(statusFilter) ? statusFilter : [];
    productVerticalFilterRef.current = Array.isArray(productVerticalFilter)
      ? productVerticalFilter
      : [];
    appliedFiltersRef.current = appliedFilters;
    startDateRef.current = startDate;
    endDateRef.current = endDate;
    raisedByFilterRef.current = raisedByFilter;
    srInvoiceFilterRef.current = srInvoiceFilter;
    complaintTypeFilterRef.current = complaintTypeFilter;
    categoryFilterRef.current = categoryFilter;
  }, [
    statusFilter,
    productVerticalFilter,
    appliedFilters,
    startDate,
    endDate,
    raisedByFilter,
    srInvoiceFilter,
    complaintTypeFilter,
    categoryFilter,
  ]);

  useEffect(() => {
    userListRef.current = Array.isArray(userList) ? userList : [];
    bvOptionsRef.current = Array.isArray(bvOptions) ? bvOptions : [];
    urlSearchParamsRef.current = urlSearchParams;
  }, [userList, bvOptions, urlSearchParams]);

  useEffect(() => {
    listLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const snapshot = readBcmServiceRequestListRestoreSnapshot();
    if (!snapshot) return;
    chartScopeStatusCsvRef.current = snapshot.chartScopeStatusCsv || null;
    syncStatusFilterFromResultsRef.current = Boolean(snapshot.syncStatusFilterFromResults);
    drilldownBvCsvRef.current = snapshot.drilldownBvCsv || null;
    drilldownBvNameRef.current = snapshot.drilldownBvName || null;
    restoreAppliedRef.current = true;
  }, []);

  const applySavedListSnapshot = (saved, userIdCode) => {
    if (!saved) return;

    chartScopeStatusCsvRef.current = saved.chartScopeStatusCsv || null;
    syncStatusFilterFromResultsRef.current = Boolean(saved.syncStatusFilterFromResults);
    drilldownBvCsvRef.current = saved.drilldownBvCsv || null;
    drilldownBvNameRef.current = saved.drilldownBvName || null;
    setPendingDashboardStatusTokens([]);

    if (Array.isArray(saved.statusFilter)) {
      statusFilterRef.current = saved.statusFilter;
      setStatusFilter(saved.statusFilter);
    }
    if (saved.categoryFilter != null) {
      categoryFilterRef.current = String(saved.categoryFilter || "");
      setCategoryFilter(String(saved.categoryFilter || ""));
    }
    if (saved.complaintTypeFilter != null) {
      complaintTypeFilterRef.current = String(saved.complaintTypeFilter || "");
      setComplaintTypeFilter(String(saved.complaintTypeFilter || ""));
    }
    if (Array.isArray(saved.productVerticalFilter)) {
      productVerticalFilterRef.current = saved.productVerticalFilter;
      setProductVerticalFilter(saved.productVerticalFilter);
    }
    if (saved.raisedByFilter != null) {
      raisedByFilterRef.current = String(saved.raisedByFilter || "");
      setRaisedByFilter(String(saved.raisedByFilter || ""));
    }
    if (saved.srInvoiceFilter != null) {
      srInvoiceFilterRef.current = String(saved.srInvoiceFilter || "");
      setSrInvoiceFilter(String(saved.srInvoiceFilter || ""));
    }
    if (saved.startDate) {
      startDateRef.current = saved.startDate;
      setStartDate(saved.startDate);
    }
    if (saved.endDate) {
      endDateRef.current = saved.endDate;
      setEndDate(saved.endDate);
    }
    if (saved.startDate && saved.endDate) {
      const displayText = `${moment(saved.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
        saved.endDate,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY")}`;
      setDateRangeText(displayText);
      if (dateRangePickerRef.current) {
        $(dateRangePickerRef.current).val(displayText);
      }
    }
    if (saved.appliedFilters) {
      const nextApplied = {
        ...saved.appliedFilters,
        createdBy: userIdCode || saved.appliedFilters.createdBy || null,
      };
      appliedFiltersRef.current = nextApplied;
      setAppliedFilters(nextApplied);
    }
    restoreAppliedRef.current = true;
  };

  // Clear stale table only when the logged-in user *changes* (not on first assign).
  // Bumping listLoadSeqRef on first assign was canceling the first dashboard→list fetch.
  useEffect(() => {
    if (!currentUserIdCode) return;
    const prev = prevUserIdForClearRef.current;
    prevUserIdForClearRef.current = currentUserIdCode;
    if (!prev || prev === currentUserIdCode) {
      if (process.env.NODE_ENV === "development") {
        console.log("[BCM SR List] currentUserIdCode:", currentUserIdCode);
      }
      return;
    }

    try {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    } catch (e) {
      // ignore
    }
    setAllData([]);
    setFilteredData([]);
    setTotalRecords(0);
    setPageNumber(1);
    lastCompletedListFetchKeyRef.current = "";
    listLoadSeqRef.current += 1;
  }, [currentUserIdCode]);

  // Bind encrypted/plain URL drill-down params + createdBy atomically before the first list API call.
  useEffect(() => {
    if (!currentUserIdCode) return;

    const shouldRestore =
      Boolean(location.state?.restoreBcmServiceRequestList) ||
      Boolean(readBcmServiceRequestListRestoreSnapshot());

    // Encrypted/plain drill-down from dashboard must win over a stale restore snapshot.
    const hasDrilldownUrl = hasBcmDrilldownSearchParams(urlSearchParams);

    if (shouldRestore && !hasDrilldownUrl) {
      const saved = readBcmServiceRequestListState();
      applySavedListSnapshot(saved, currentUserIdCode);
      urlFiltersBoundRef.current = true;
      setUrlFiltersBound(true);
      return;
    }

    if (shouldRestore && hasDrilldownUrl) {
      // Drop stale restore so dashboard chart/card navigation is applied.
      clearBcmServiceRequestListState();
    }

    // Bind atomically — do not flip urlFiltersBound false first (that aborts list load).
    // Skip full re-bind when this URL was already applied (Strict Mode / parent re-render).
    if (
      urlFiltersBoundRef.current &&
      lastBoundUrlSearchKeyRef.current === urlSearchKey &&
      String(appliedFiltersRef.current?.createdBy || "") === String(currentUserIdCode || "")
    ) {
      return;
    }
    lastBoundUrlSearchKeyRef.current = urlSearchKey;
    lastCompletedListFetchKeyRef.current = "";
    inFlightListFetchKeyRef.current = null;

    const statusParam = urlSearchParams.get("status");
    const statusSelectedParam = urlSearchParams.get("statusSelected"); // dashboard-selected SRSTATUSMASTER ids (CSV)
    const categoryParam = urlSearchParams.get("category");
    const valueSlabParam = urlSearchParams.get("valueSlab");
    const fromDateParam = urlSearchParams.get("fromDate");
    const toDateParam = urlSearchParams.get("toDate");
    const queryRaisedParam = urlSearchParams.get("queryRaised");
    const onlyQueriesRaisedByMe =
      queryRaisedParam === "true" ||
      queryRaisedParam === "1" ||
      String(queryRaisedParam || "").toLowerCase() === "true";
    const queryToRespondParam = urlSearchParams.get("queryToRespond");
    const onlyQueriesToRespondByMe =
      queryToRespondParam === "true" ||
      queryToRespondParam === "1" ||
      String(queryToRespondParam || "").toLowerCase() === "true";
    const specialCaseParam = urlSearchParams.get("specialCase");
    const onlySpecialCase =
      specialCaseParam === "true" ||
      specialCaseParam === "1" ||
      String(specialCaseParam || "").toLowerCase() === "true";
    const slaBreachedParam = urlSearchParams.get("slaBreached");
    const onlySlaBreached =
      slaBreachedParam === "true" ||
      slaBreachedParam === "1" ||
      String(slaBreachedParam || "").toLowerCase() === "true";

    const parseCsv = (v) =>
      String(v || "")
        .split(",")
        .map((s) => decodeURIComponent(String(s || "")).trim())
        .filter(Boolean);

    let statusPatch = {};
    if (statusParam) {
      const parts = parseCsv(statusParam);
      if (parts.length > 0) {
        const urlStatus = parts[0];
        const isDashboardNumeric =
          /^\d+$/.test(urlStatus) &&
          ["2", "3", "4", "5", "7"].includes(urlStatus);
        const allDashboardNumeric =
          parts.length > 1 &&
          parts.every(
            (p) => /^\d+$/.test(p) && ["2", "3", "4", "5", "7"].includes(p)
          );

        if (allDashboardNumeric) {
          // API uses chart drill-down scope via value slab / BV / category; UI selects all statuses.
          chartScopeStatusCsvRef.current = parts.join(",");
          syncStatusFilterFromResultsRef.current = true;
          setPendingDashboardStatusTokens([]);
          setStatusFilter([]);
          statusPatch = { statusCode: null, statusCodesCsv: null };
        } else if (isDashboardNumeric) {
          chartScopeStatusCsvRef.current = null;
          urlDashboardStatusTokenRef.current = urlStatus;
          setPendingDashboardStatusTokens([urlStatus]);
          setStatusFilter([]);
          statusPatch = { statusCode: urlStatus, statusCodesCsv: null };
        } else if (parts.length === 1) {
          chartScopeStatusCsvRef.current = null;
          urlDashboardStatusTokenRef.current = null;
          setPendingDashboardStatusTokens([]);
          setStatusFilter([urlStatus]);
          statusPatch = { statusCode: urlStatus, statusCodesCsv: null };
        } else {
          chartScopeStatusCsvRef.current = null;
          urlDashboardStatusTokenRef.current = null;
          setPendingDashboardStatusTokens([]);
          setStatusFilter(parts);
          statusPatch = { statusCode: null, statusCodesCsv: parts.join(",") };
        }
      }
    } else {
      urlDashboardStatusTokenRef.current = null;
      setPendingDashboardStatusTokens([]);
      // Chart aggregate drill-down (category/BV/value slab) must match dashboard totals:
      // pending queue + activity already done by this approver (approved/rejected/closed).
      const isChartAggregateDrilldown =
        !onlyQueriesRaisedByMe &&
        !onlyQueriesToRespondByMe &&
        !onlySpecialCase &&
        !onlySlaBreached &&
        (Boolean(categoryParam) ||
          Boolean(parseBusinessVerticalCsvFromSearchParams(urlSearchParams)) ||
          Boolean(parseBusinessVerticalNameFromSearchParams(urlSearchParams)) ||
          Boolean(valueSlabParam));
      if (isChartAggregateDrilldown) {
        chartScopeStatusCsvRef.current = CHART_SCOPE_STATUS_TOKENS.join(",");
        syncStatusFilterFromResultsRef.current = true;
        setPendingDashboardStatusTokens([]);
        setStatusFilter([]);
        statusPatch = {
          statusCode: null,
          statusCodesCsv: null,
        };
      } else {
        chartScopeStatusCsvRef.current = null;
        setStatusFilter([]);
        statusPatch = { statusCode: null, statusCodesCsv: null };
      }
    }

    // Bind dashboard-selected status ids — skip for card drill-downs (query / SLA breached).
    const statusSelectedIds = parseCsv(statusSelectedParam);
    if (statusSelectedIds.length > 0 && !onlyQueriesRaisedByMe && !onlyQueriesToRespondByMe && !onlySpecialCase && !onlySlaBreached) {
      setStatusFilter((prev) => {
        const hasPrev = Array.isArray(prev) && prev.length > 0;
        return hasPrev ? prev : statusSelectedIds;
      });
    }

    let businessVerticalCodesCsv = parseBusinessVerticalCsvFromSearchParams(urlSearchParams);
    const businessVerticalName = parseBusinessVerticalNameFromSearchParams(urlSearchParams);
    if (!businessVerticalCodesCsv && businessVerticalName) {
      businessVerticalCodesCsv = businessVerticalName;
    }
    if (businessVerticalCodesCsv) {
      drilldownBvCsvRef.current = businessVerticalCodesCsv;
      setProductVerticalFilter(businessVerticalCodesCsv.split(",").filter(Boolean));
    } else {
      drilldownBvCsvRef.current = null;
    }
    drilldownBvNameRef.current = businessVerticalName || businessVerticalCodesCsv || null;

    if (categoryParam) {
      const decoded = decodeURIComponent(categoryParam);
      setCategoryFilter(decoded);
      setComplaintTypeFilter(decoded);
    }

    const valueSlabDecoded = valueSlabParam ? decodeURIComponent(valueSlabParam).trim() : "";

    const from = fromDateParam ? decodeURIComponent(fromDateParam) : "";
    const to = toDateParam ? decodeURIComponent(toDateParam) : "";
    const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
    const fromIso = isIso(from) ? from : null;
    const toIso = isIso(to) ? to : null;

    if (fromIso) setStartDate(fromIso);
    if (toIso) setEndDate(toIso);
    if (fromIso && toIso) {
      const displayText = `${moment(fromIso, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
        toIso,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY")}`;
      setDateRangeText(displayText);
      if (dateRangePickerRef.current) {
        $(dateRangePickerRef.current).val(displayText);
      }
    }

    setAppliedFilters((prev) => {
      const next = {
        createdBy: currentUserIdCode,
        onlyQueriesRaisedByMe,
        onlyQueriesToRespondByMe,
        onlySpecialCase,
        onlySlaBreached,
        ...statusPatch,
        businessVerticalCodesCsv: businessVerticalCodesCsv || null,
        valueSlab: valueSlabDecoded || null,
        fromDate: fromIso || prev.fromDate,
        toDate: toIso || prev.toDate,
        raisedByUserIdCode: prev.raisedByUserIdCode,
        searchText: prev.searchText,
      };
      if (
        prev.createdBy === next.createdBy &&
        Boolean(prev.onlyQueriesRaisedByMe) === Boolean(next.onlyQueriesRaisedByMe) &&
        Boolean(prev.onlyQueriesToRespondByMe) === Boolean(next.onlyQueriesToRespondByMe) &&
        Boolean(prev.onlySpecialCase) === Boolean(next.onlySpecialCase) &&
        Boolean(prev.onlySlaBreached) === Boolean(next.onlySlaBreached) &&
        String(prev.statusCode || "") === String(next.statusCode || "") &&
        String(prev.statusCodesCsv || "") === String(next.statusCodesCsv || "") &&
        String(prev.businessVerticalCodesCsv || "") ===
          String(next.businessVerticalCodesCsv || "") &&
        String(prev.valueSlab || "") === String(next.valueSlab || "") &&
        String(prev.fromDate || "") === String(next.fromDate || "") &&
        String(prev.toDate || "") === String(next.toDate || "")
      ) {
        return prev;
      }
      return next;
    });

    urlFiltersBoundRef.current = true;
    setUrlFiltersBound(true);
  }, [
    urlSearchKey,
    currentUserIdCode,
    location.state?.restoreBcmServiceRequestList,
  ]);

  // Map single dashboard status token (pie segment click) to one SRSTATUSMASTER checkbox.
  useEffect(() => {
    if (!Array.isArray(pendingDashboardStatusTokens) || pendingDashboardStatusTokens.length !== 1) {
      return;
    }
    if (!Array.isArray(statusOptionsFromApi) || statusOptionsFromApi.length === 0) return;

    const masterIds = mapDashboardTokensToStatusMasterIds(
      pendingDashboardStatusTokens,
      statusOptionsFromApi
    );
    if (masterIds.length === 0) {
      setPendingDashboardStatusTokens([]);
      return;
    }

    const selectedCsv = urlSearchParams.get("statusSelected");
    const selectedIds = selectedCsv
      ? String(selectedCsv)
          .split(",")
          .map((s) => decodeURIComponent(String(s || "")).trim())
          .filter(Boolean)
      : [];

    if (selectedIds.length > 0) {
      const selectedLower = new Set(selectedIds.map((v) => v.toLowerCase()));
      setStatusFilter(masterIds.filter((id) => selectedLower.has(id.toLowerCase())));
    } else {
      setStatusFilter(masterIds);
    }

    setPendingDashboardStatusTokens([]);
  }, [pendingDashboardStatusTokens, statusOptionsFromApi, urlSearchKey, urlSearchParams]);

  // Chart drill-down (BV / value slab / category): select all status checkboxes once options are loaded.
  useEffect(() => {
    if (!syncStatusFilterFromResultsRef.current) return;
    if (!urlFiltersBound) return;
    if (!Array.isArray(statusOptionsFromApi) || statusOptionsFromApi.length === 0) return;

    syncStatusFilterFromResultsRef.current = false;
    const allIds = getAllStatusMasterIds(statusOptionsFromApi);
    const currentIds = (statusFilter || []).map((v) => String(v ?? "").trim()).filter(Boolean);
    if (!statusIdSetsEqual(allIds, currentIds)) {
      // Only update checkboxes — do not suppress / skip the list load.
      setStatusFilter(allIds);
    }
  }, [urlFiltersBound, statusOptionsFromApi, statusFilter]);

  // When BV master options load, resolve chart drill-down name → CATGCODE(s) for API + dropdown.
  useEffect(() => {
    if (!urlFiltersBound) return;
    const drillCsv = drilldownBvCsvRef.current;
    const drillName = drilldownBvNameRef.current;
    if (!drillCsv && !drillName) return;
    if (!Array.isArray(bvOptions) || bvOptions.length === 0) return;

    const resolved = buildBusinessVerticalMatchContext(
      drillCsv || drillName,
      drillName || drillCsv,
      bvOptions
    );
    if (!resolved.codesCsv) return;

    const ids = resolved.codesCsv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const currentKey = (productVerticalFilter || [])
      .map((s) => String(s).trim())
      .filter(Boolean)
      .sort()
      .join(",");
    const nextKey = [...ids].sort().join(",");
    if (currentKey === nextKey) return;

    drilldownBvCsvRef.current = resolved.codesCsv;
    productVerticalFilterRef.current = ids;
    setProductVerticalFilter(ids);
    // Do not update appliedFilters here — that re-triggers the list load / aborts.
  }, [bvOptions, urlFiltersBound, urlSearchKey, productVerticalFilter]);

  // Stable delegated click handler (DataTables scrollX moves tbody; wrapper + void href avoids # conflicts).
  useEffect(() => {
    const wrap = srTableWrapperRef.current;
    if (!wrap) return undefined;

    const onSrLinkClick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      const $link = $(e.target).closest(".sr-link");
      if (!$link.length) return;
      const parentSrCode = String($link.attr("data-parent-sr-code") || "").trim();
      const childSrCode = String($link.attr("data-child-sr-code") || "").trim();
      if (!parentSrCode && !childSrCode) return;
      const parent = parentSrCode || childSrCode;
      const queryParts = ["fromList=bcm"];
      if (childSrCode) {
        queryParts.unshift(`childSrCode=${encodeURIComponent(childSrCode)}`);
      }
      const query = `?${queryParts.join("&")}`;

      markComplaintsListRestored();
      saveBcmServiceRequestListState({
        pendingRestore: true,
        statusFilter: Array.isArray(statusFilterRef.current)
          ? [...statusFilterRef.current]
          : [],
        categoryFilter: categoryFilterRef.current,
        complaintTypeFilter: complaintTypeFilterRef.current,
        productVerticalFilter: Array.isArray(productVerticalFilterRef.current)
          ? [...productVerticalFilterRef.current]
          : [],
        raisedByFilter: raisedByFilterRef.current,
        srInvoiceFilter: srInvoiceFilterRef.current,
        startDate: startDateRef.current,
        endDate: endDateRef.current,
        appliedFilters: appliedFiltersRef.current
          ? { ...appliedFiltersRef.current }
          : null,
        chartScopeStatusCsv: chartScopeStatusCsvRef.current,
        syncStatusFilterFromResults: syncStatusFilterFromResultsRef.current,
        drilldownBvCsv: drilldownBvCsvRef.current,
        drilldownBvName: drilldownBvNameRef.current,
        returnPath: `${listLocationRef.current.pathname || "/bcm-service-requests"}${
          listLocationRef.current.search || ""
        }`,
      });
      navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
    };

    const $wrap = $(wrap);
    $wrap.on("click.bcmSrNav", ".sr-link", onSrLinkClick);
    return () => {
      $wrap.off("click.bcmSrNav", ".sr-link", onSrLinkClick);
    };
    // Rebind when the table mounts (Users-style: table only renders when rows exist).
  }, [navigate, allData.length]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      suppressLoadForStatusUiSyncRef.current = false;

      const effectiveCreatedBy =
        (appliedFilters.createdBy || currentUserIdCode || "")
          .toString()
          .trim();
      const filtersBound = urlFiltersBoundRef.current || urlFiltersBound;
      if (!effectiveCreatedBy || !filtersBound) {
        return;
      }

      const searchParams = urlSearchParamsRef.current;
      const fetchKey = JSON.stringify({
        createdBy: effectiveCreatedBy,
        appliedFilters: {
          statusCode: appliedFilters.statusCode,
          statusCodesCsv: appliedFilters.statusCodesCsv,
          businessVerticalCodesCsv: appliedFilters.businessVerticalCodesCsv,
          valueSlab: appliedFilters.valueSlab,
          fromDate: appliedFilters.fromDate,
          toDate: appliedFilters.toDate,
          raisedByUserIdCode: appliedFilters.raisedByUserIdCode,
          searchText: appliedFilters.searchText,
          onlyQueriesRaisedByMe: appliedFilters.onlyQueriesRaisedByMe,
          onlyQueriesToRespondByMe: appliedFilters.onlyQueriesToRespondByMe,
          onlySpecialCase: appliedFilters.onlySpecialCase,
          onlySlaBreached: appliedFilters.onlySlaBreached,
        },
        urlSearchKey,
      });

      if (fetchKey === lastCompletedListFetchKeyRef.current) {
        return;
      }

      inFlightListFetchKeyRef.current = fetchKey;
      setListFetchDone(false);

      try {
        const t0 =
          typeof performance !== "undefined" && typeof performance.now === "function"
            ? performance.now()
            : Date.now();
        const listApi = complaintAPI.getServiceRequestListApprover;

        const isCardDrilldown =
          appliedFilters.onlyQueriesRaisedByMe ||
          appliedFilters.onlyQueriesToRespondByMe ||
          appliedFilters.onlySpecialCase ||
          appliedFilters.onlySlaBreached;
        const statusIds = Array.isArray(statusFilterRef.current)
          ? statusFilterRef.current.map((v) => String(v ?? "").trim()).filter(Boolean)
          : [];
        const urlStatusToken = urlDashboardStatusTokenRef.current;
        let statusCodeForApi =
          (urlStatusToken && isDashboardStatusToken(urlStatusToken)
            ? String(urlStatusToken).trim()
            : null) ||
          appliedFilters.statusCode ||
          null;
        let statusCodesCsvForApi = statusCodeForApi
          ? null
          : appliedFilters.statusCodesCsv || null;
        if (isCardDrilldown) {
          statusCodeForApi = null;
          statusCodesCsvForApi = null;
        } else {
          const chartScopeCsv = chartScopeStatusCsvRef.current;
          const urlBvCsv =
            drilldownBvCsvRef.current ||
            parseBusinessVerticalCsvFromSearchParams(searchParams);
          const urlBvName =
            drilldownBvNameRef.current ||
            parseBusinessVerticalNameFromSearchParams(searchParams);
          const urlCategoryParam = searchParams.get("category");
          const isChartAggregateDrilldown =
            !appliedFilters.onlyQueriesRaisedByMe &&
            !appliedFilters.onlyQueriesToRespondByMe &&
            !appliedFilters.onlySpecialCase &&
            !appliedFilters.onlySlaBreached &&
            (Boolean(chartScopeCsv) ||
              Boolean(appliedFilters.valueSlab) ||
              Boolean(urlCategoryParam) ||
              Boolean(urlBvCsv || urlBvName));
          const useSpChartDrilldownScope =
            Boolean(appliedFilters.valueSlab) ||
            Boolean(urlBvCsv || urlBvName);
          const appliedIsDashboardToken =
            isDashboardStatusToken(appliedFilters.statusCode) ||
            (appliedFilters.statusCodesCsv &&
              String(appliedFilters.statusCodesCsv)
                .split(",")
                .filter((s) => String(s || "").trim())
                .every(isDashboardStatusToken));
          if (useSpChartDrilldownScope && !appliedIsDashboardToken) {
            statusCodeForApi = null;
            statusCodesCsvForApi = null;
          } else if (appliedIsDashboardToken) {
            statusCodeForApi = isDashboardStatusToken(appliedFilters.statusCode)
              ? String(appliedFilters.statusCode).trim()
              : null;
            statusCodesCsvForApi = statusCodeForApi
              ? null
              : appliedFilters.statusCodesCsv || null;
          } else if (isChartAggregateDrilldown) {
            statusCodeForApi = null;
            statusCodesCsvForApi = CHART_SCOPE_STATUS_TOKENS.join(",");
          } else if (chartScopeCsv) {
            statusCodeForApi = null;
            statusCodesCsvForApi = chartScopeCsv;
          } else if (statusIds.length === 1) {
            statusCodeForApi = statusIds[0];
            statusCodesCsvForApi = null;
          } else if (statusIds.length > 1) {
            statusCodeForApi = null;
            statusCodesCsvForApi = statusIds.join(",");
          } else if (
            statusIds.length === 0 &&
            appliedFilters.statusCodesCsv &&
            String(appliedFilters.statusCodesCsv)
              .split(",")
              .every(isDashboardStatusToken)
          ) {
            statusCodeForApi = null;
            statusCodesCsvForApi = appliedFilters.statusCodesCsv;
          }
          if (urlStatusToken && isDashboardStatusToken(urlStatusToken)) {
            statusCodeForApi = String(urlStatusToken).trim();
            statusCodesCsvForApi = null;
          }
        }

        const urlBvCsv =
          drilldownBvCsvRef.current ||
          parseBusinessVerticalCsvFromSearchParams(searchParams);
        const urlBvName =
          drilldownBvNameRef.current ||
          parseBusinessVerticalNameFromSearchParams(searchParams);
        const rawBvCsv =
          appliedFilters.businessVerticalCodesCsv ||
          drilldownBvCsvRef.current ||
          urlBvCsv ||
          null;
        const rawBvName = drilldownBvNameRef.current || urlBvName || null;
        const bvDrilldownActive = Boolean(rawBvCsv || rawBvName);
        const bvMatch = buildBusinessVerticalMatchContext(
          rawBvCsv,
          rawBvName,
          bvOptionsRef.current
        );
        const businessVerticalCodesCsv =
          bvMatch.codesCsv || rawBvCsv || rawBvName || undefined;

        const useStatusFastPath =
          isDashboardStatusToken(statusCodeForApi) ||
          (statusCodesCsvForApi &&
            String(statusCodesCsvForApi)
              .split(",")
              .filter((s) => String(s || "").trim())
              .every(isDashboardStatusToken) &&
            String(statusCodesCsvForApi)
              .split(",")
              .filter((s) => String(s || "").trim()).length === 1);
        const apiBusinessVerticalCodesCsv = businessVerticalCodesCsv;
        const apiValueSlab = useStatusFastPath
          ? undefined
          : appliedFilters.valueSlab || undefined;

        const urlCategoryParamForFanOut = searchParams.get("category");
        // Category / multi-status chart scope: fan out Pending/Action fast paths (statusCode=2|3|4|5).
        // A single statusCodesCsv=2,3,4,5 call hits a slow SP path on remote API and timed out at 15s.
        const multiChartTokens = (
          urlCategoryParamForFanOut
            ? CHART_SCOPE_STATUS_TOKENS
            : String(statusCodesCsvForApi || "")
                .split(",")
                .map((s) => String(s || "").trim())
                .filter((s) => isDashboardStatusToken(s))
        ).filter(Boolean);
        const useParallelStatusFastPaths =
          !apiValueSlab &&
          !apiBusinessVerticalCodesCsv &&
          !appliedFilters.onlyQueriesRaisedByMe &&
          !appliedFilters.onlyQueriesToRespondByMe &&
          !appliedFilters.onlySpecialCase &&
          !appliedFilters.onlySlaBreached &&
          multiChartTokens.length > 1 &&
          (Boolean(urlCategoryParamForFanOut) ||
            multiChartTokens.length ===
              String(statusCodesCsvForApi || "")
                .split(",")
                .map((s) => String(s || "").trim())
                .filter(Boolean).length);

        if (process.env.NODE_ENV === "development") {
          console.log("[BCM SR List] API filters:", {
            businessVerticalCodesCsv: apiBusinessVerticalCodesCsv,
            useStatusFastPath,
            useParallelStatusFastPaths,
            fanOutTokens: useParallelStatusFastPaths ? multiChartTokens : undefined,
            fromDate: appliedFilters.fromDate,
            toDate: appliedFilters.toDate,
            statusCode: statusCodeForApi,
            statusCodesCsv: statusCodesCsvForApi,
            category: searchParams.get("category"),
          });
        }

        const commonListParams = {
          createdBy: effectiveCreatedBy,
          businessVerticalCodesCsv: apiBusinessVerticalCodesCsv,
          valueSlab: apiValueSlab,
          fromDate: appliedFilters.fromDate || undefined,
          toDate: appliedFilters.toDate || undefined,
          raisedByUserIdCode: appliedFilters.raisedByUserIdCode || undefined,
          searchText: appliedFilters.searchText || undefined,
          queryRaised: appliedFilters.onlyQueriesRaisedByMe || undefined,
          queryToRespond: appliedFilters.onlyQueriesToRespondByMe || undefined,
          specialCase: appliedFilters.onlySpecialCase || undefined,
          slaBreached: appliedFilters.onlySlaBreached || undefined,
          pageNumber: 1,
          pageSize: 50,
          // Remote SP can exceed 15s when pool is busy after dashboard; do not abort early.
          timeout: 60000,
        };

        const { rawItems, total } = await fetchApproverListShared(fetchKey, async () => {
          if (useParallelStatusFastPaths) {
            // Sequential fan-out: each statusCode hits Pending/Action fast path.
            // Avoids slow statusCodesCsv=2,3,4,5 mega-call (remote 15s+ timeouts).
            const byKey = new Map();
            let paintedEarly = false;
            for (const token of multiChartTokens) {
              try {
                const res = await listApi({
                  ...commonListParams,
                  statusCode: token,
                  statusCodesCsv: undefined,
                  businessVerticalCodesCsv: apiBusinessVerticalCodesCsv,
                  valueSlab: apiValueSlab,
                });
                const batch =
                  res?.success && res?.data?.items && Array.isArray(res.data.items)
                    ? res.data.items
                    : [];
                batch.forEach((d) => {
                  const srCode = d.srCode || d.SRCode || "";
                  const child =
                    d.childSRNumber || d.ChildSRNumber || d.srNo || d.SRNo || "";
                  const inv = d.invoiceNumber || d.InvoiceNumber || "";
                  const key = `${srCode}|${child}|${inv}`.toLowerCase();
                  if (!byKey.has(key)) byKey.set(key, d);
                });
                // Show Pending rows immediately (token "2") so first dashboard click isn't blank.
                if (!paintedEarly && token === "2" && byKey.size > 0 && !cancelled) {
                  paintedEarly = true;
                  const earlyItems = Array.from(byKey.values());
                  setAllData(earlyItems);
                  setFilteredData(earlyItems);
                  setListFetchDone(true);
                }
              } catch (err) {
                console.warn("[BCM SR List] status fan-out failed:", token, err);
              }
            }
            return { rawItems: Array.from(byKey.values()), total: byKey.size };
          }

          const res = await listApi({
            ...commonListParams,
            statusCode: statusCodeForApi || undefined,
            statusCodesCsv: statusCodesCsvForApi || undefined,
          });
          const items =
            res?.success && res?.data?.items && Array.isArray(res.data.items)
              ? res.data.items
              : [];
          if (res && res.success === false && process.env.NODE_ENV === "development") {
            console.warn("[BCM SR List] list API returned success:false", res);
          }
          return { rawItems: items, total: res?.data?.totalRecords ?? 0 };
        });

        // Stale Strict Mode run — keep HTTP (no abort) but do not paint.
        if (cancelled) return;

        // Map sp_GetServiceRequestList response to table row shape (no extra fields)
        const mapped = rawItems.map((d) => {
          const srCode = d.srCode || d.SRCode || "";
          const srNo = d.srNo || d.SRNo || d.srNumber || d.SRNumber || srCode;
          const status =
            d.currentStatus ||
            d.CurrentStatus ||
            d.statusName ||
            d.StatusName ||
            d.status ||
            d.Status ||
            "";
          const raisedBy = d.raisedBy || d.RaisedBy || "";
          const raisedByUserIdCode =
            d.raisedByUserIdCode ||
            d.RaisedByUserIdCode ||
            d.createdBy ||
            d.CreatedBy ||
            "";
          // PERMANENT: Assign To display string comes from Greenlam.fn_GetCurrentAssignee
          // (API AssignedTo / AssignedToName). Do not rebuild from visibility fields first.
          const assignedTo = d.assignedTo || d.AssignedTo || "";
          const assignToRoleId = d.assignToRoleId || d.AssignToRoleId || "";
          const assignToUserTypeCode = d.assignToUserTypeCode || d.AssignToUserTypeCode || "";
          const assignToUserFullName = d.assignToUserFullName || d.AssignToUserFullName || "";
          const assignToUserName = d.assignToUserName || d.AssignToUserName || "";
          const assignedToName =
            (assignedTo && String(assignedTo).trim()) ||
            (assignToUserFullName && assignToRoleId
              ? `${assignToUserFullName} (${assignToRoleId})`
              : assignToUserFullName || assignToUserName || "");
          const srCategory =
            d.srCategory ||
            d.SRCategory ||
            d.complaintTypeName ||
            d.ComplaintTypeName ||
            "";
          const category =
            d.businessVertical ||
            d.BusinessVertical ||
            d.productCategory ||
            d.ProductCategory ||
            d.category ||
            d.Category ||
            "";
          return {
            id: srCode,
            srCode,
            srNumber: srNo,
            statusName: status,
            statusCode: status,
            createdByName: raisedBy,
            raisedByUserIdCode,
            assignedToName,
            complaintTypeName: srCategory,
            productCategory: category,
            invoiceNumber: d.invoiceNumber || d.InvoiceNumber || "",
            createdDate: d.srDate || d.SRDate || d.invoiceDate || d.InvoiceDate,
            assignToRoleId,
            assignToUserTypeCode,
            assignToUserFullName,
            slNo: d.slNo ?? d.SLNo,
          };
        });

        // BV chart drill-down: client filter only when SP did not receive BV
        // (status-token drill-downs send BV to SP; avoid double-filter wiping rows).
        let items = mapped;
        if (bvDrilldownActive && !apiBusinessVerticalCodesCsv) {
          const before = items.length;
          items = filterItemsByBusinessVertical(items, bvMatch);
          if (process.env.NODE_ENV === "development") {
            console.log("[BCM SR List] BV client filter:", before, "->", items.length, bvMatch);
          }
        }
        if (appliedFilters.raisedByUserIdCode) {
          const sel = String(appliedFilters.raisedByUserIdCode).trim().toLowerCase();
          // Prefer filtering by RaisedByUserIdCode when API provides it.
          const hasCode = items.some((it) => String(it.raisedByUserIdCode || "").trim());
          if (hasCode) {
            items = items.filter(
              (item) => String(item.raisedByUserIdCode || "").trim().toLowerCase() === sel
            );
          } else {
            // Fallback: filter by displayed RaisedBy text (e.g. "Full Name ( Role )").
            // This keeps the filter usable even when backend hasn't yet been deployed with RaisedByUserIdCode.
            const u = (userListRef.current || []).find((x) => {
              const id = (x.userIdCode || x.UserIdCode || "").toString().trim().toLowerCase();
              return id === sel;
            });
            const name =
              (u?.fullName ||
                u?.FullName ||
                u?.displayName ||
                u?.DisplayName ||
                u?.userName ||
                u?.UserName ||
                "")
                .toString()
                .trim()
                .toLowerCase();
            if (name) {
              items = items.filter((item) => {
                const rb = String(item.createdByName || "").toLowerCase();
                // Match "name (" prefix or exact equals.
                return rb === name || rb.startsWith(`${name} (`) || rb.startsWith(name + " (");
              });
            }
          }
        }
        const complaintType =
          String(complaintTypeFilterRef.current || "").trim() ||
          String(searchParams.get("category") || "").trim();
        if (complaintType) {
          items = items.filter(
            (item) =>
              (item.complaintTypeName || "").toLowerCase() ===
              complaintType.toLowerCase()
          );
        }

        setAllData(items);
        setFilteredData(items);
        setTotalRecords(
          bvDrilldownActive || useParallelStatusFastPaths || Boolean(complaintType)
            ? items.length
            : total
        );
        setListFetchDone(true);
        lastCompletedListFetchKeyRef.current = fetchKey;

        if (process.env.NODE_ENV === "development") {
          const apiMs =
            typeof performance !== "undefined" && typeof performance.now === "function"
              ? Math.round(performance.now() - t0)
              : null;
          console.log("[BCM SR List] list API done", {
            apiMs,
            rows: items.length,
            total: bvDrilldownActive ? items.length : total,
            statusCode: statusCodeForApi,
            statusCodesCsv: statusCodesCsvForApi,
            category: complaintType || null,
          });
        }
      } catch (err) {
        if (
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError" ||
          err?.name === "AbortError"
        ) {
          return;
        }
        console.error("Failed to load SRs", err);
        if (!cancelled) {
          lastCompletedListFetchKeyRef.current = "";
          setAllData([]);
          setFilteredData([]);
          setTotalRecords(0);
          setListFetchDone(true);
        }
      } finally {
        if (inFlightListFetchKeyRef.current === fetchKey) {
          inFlightListFetchKeyRef.current = null;
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // bvOptions is read via bvOptionsRef — do not re-run list SP when BV master arrives.
  }, [appliedFilters, urlFiltersBound, currentUserIdCode, urlSearchKey]);

  // Match /csrp/users: destroy + recreate DataTable when rows change (pageLength 25 + chrome wrappers).
  useEffect(() => {
    if (!Array.isArray(allData) || allData.length === 0) {
      if (dataTableInstanceRef.current) {
        try {
          dataTableInstanceRef.current.destroy();
        } catch (_) {
          /* ignore */
        }
        dataTableInstanceRef.current = null;
      }
      return;
    }
    if (!srTableRef.current) return;

    if (dataTableInstanceRef.current) {
      try {
        dataTableInstanceRef.current.destroy();
      } catch (_) {
        /* ignore */
      }
      dataTableInstanceRef.current = null;
    }

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(srTableRef.current).DataTable({
        data: allData,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        order: [[1, "desc"]], // SR Date DESC by default (newest first)
        destroy: true,
        scrollX: true,
        autoWidth: false,
        deferRender: true,
        pageLength: 25,
        columns: [
          {
            title: "SR NO",
            data: null,
            render: (d) => {
              const num = d.srNumber || d.srNo || d.srCode || d.id || "";
              return buildSrLinkHtml(d, num);
            },
            className: "text-center",
            createdCell: (td) => $(td).addClass("text-center"),
          },
          {
            title: "SR Date",
            data: "createdDate",
            render: (date, type) => {
              if (!date) return type === "sort" || type === "type" ? 0 : "-";
              const m = moment(date);
              if (!m.isValid()) return type === "sort" || type === "type" ? 0 : "-";
              if (type === "sort" || type === "type") return m.valueOf();
              return m.format("DD-MM-YYYY");
            },
            className: "text-center",
          },
          {
            title: "INVOICE NO",
            data: "invoiceNumber",
            render: (v) => v || "-",
            className: "text-center",
          },
          {
            title: "BUSINESS VERTICAL",
            data: "productCategory",
            render: (v) => v || "",
          },
          {
            title: "SR CATEGORY",
            data: "complaintTypeName",
            render: (v) => v || "",
          },
          {
            title: "RAISED BY",
            data: "createdByName",
            render: (v) => v || "",
          },
          {
            title: "Assigned To / Last Actioned By",
            data: null,
            // PERMANENT: prefer API AssignedTo from fn_GetCurrentAssignee
            render: (d) => {
              if (d.assignedToName) return d.assignedToName;
              const code = d.assignToRoleId || "";
              const name = d.assignToUserFullName || d.assignToUserName || "";
              if (name && code) return `${name} (${code})`;
              if (name) return name;
              if (code) return code;
              return "-";
            },
          },
          {
            title: "STATUS",
            data: "statusName",
            render: (status) => {
              const s = (status || "").toString();
              const normalizedStatus = String(s).trim().toLowerCase();
              let color = "#000000";
              if (normalizedStatus === "open") color = "#1976d2";
              else if (normalizedStatus === "rejected") color = "#ff0000";
              else if (
                normalizedStatus === "pending" ||
                normalizedStatus.includes("pending")
              )
                color = "#ff9800";
              else if (
                normalizedStatus === "approved" ||
                normalizedStatus === "reopen" ||
                normalizedStatus === "closed"
              )
                color = "#4caf50";
              return `<span style="color: ${color}; font-weight: 600;">${escapeHtmlAttr(s || "-")}</span>`;
            },
          },
        ],
        initComplete: function () {
          try {
            const wrapper = $(srTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            const dtScrollDiv = $(srTableRef.current).closest(".dt-scroll");
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(srTableRef.current).closest(".table-responsive");
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
      });
    } catch (err) {
      console.error("[BCM SR List] DataTable init error", err);
    }
  }, [allData]);

  // Destroy DataTable only when leaving the page.
  useEffect(() => {
    return () => {
      if (dataTableInstanceRef.current) {
        try {
          dataTableInstanceRef.current.destroy();
        } catch (_) {
          /* ignore */
        }
        dataTableInstanceRef.current = null;
      }
    };
  }, []);

  // Apply filters: send to API (sp_GetServiceRequestList) and reload first page
  const handleFilter = () => {
    lastCompletedListFetchKeyRef.current = "";
    chartScopeStatusCsvRef.current = null;
    urlDashboardStatusTokenRef.current = null;
    syncStatusFilterFromResultsRef.current = false;
    const bvCsv =
      Array.isArray(productVerticalFilter) && productVerticalFilter.length > 0
        ? productVerticalFilter
            .map((v) => String(v ?? "").trim())
            .filter(Boolean)
            .join(",")
        : null;
    const statusIds = (statusFilter || [])
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    let statusCode = null;
    let statusCodesCsv = null;
    if (statusIds.length === 1) {
      statusCode = statusIds[0];
    } else if (statusIds.length > 1) {
      statusCodesCsv = statusIds.join(",");
    }

    // Card drill-downs (Special Case / Query / SLA breached) never send a status to the SP. When the user
    // picks specific statuses (not "all"), leave the card scope so the status search runs on all SRs.
    const allStatusIds = getAllStatusMasterIds(statusOptionsFromApi);
    const pickedSpecificStatus =
      statusIds.length > 0 &&
      !(allStatusIds.length > 0 && statusIdSetsEqual(allStatusIds, statusIds));

    setAppliedFilters((prev) => ({
      createdBy: currentUserIdCode,
      statusCode,
      statusCodesCsv,
      businessVerticalCodesCsv: bvCsv,
      valueSlab: prev.valueSlab, // preserve drill-down slab unless user changes via URL
      fromDate: startDate && startDate.trim() ? startDate.trim() : null,
      toDate: endDate && endDate.trim() ? endDate.trim() : null,
      raisedByUserIdCode: raisedByFilter && raisedByFilter.trim() ? raisedByFilter.trim() : null,
      searchText:
        srInvoiceFilter && srInvoiceFilter.trim() ? srInvoiceFilter.trim() : null,
      onlyQueriesRaisedByMe: pickedSpecificStatus ? false : prev.onlyQueriesRaisedByMe,
      onlyQueriesToRespondByMe: pickedSpecificStatus ? false : prev.onlyQueriesToRespondByMe,
      onlySpecialCase: pickedSpecificStatus ? false : prev.onlySpecialCase,
      onlySlaBreached: pickedSpecificStatus ? false : prev.onlySlaBreached,
    }));
    setPageNumber(1);
  };

  const handleReset = () => {
    lastCompletedListFetchKeyRef.current = "";
    clearBcmServiceRequestListState();
    chartScopeStatusCsvRef.current = null;
    urlDashboardStatusTokenRef.current = null;
    syncStatusFilterFromResultsRef.current = false;
    suppressLoadForStatusUiSyncRef.current = false;
    setStatusFilter([]);
    setCategoryFilter("");
    setComplaintTypeFilter("");
    setProductVerticalFilter([]);
    setRaisedByFilter("");
    setSrInvoiceFilter("");
    const fy = getCurrentFinancialYearRange();
    const defStart = fy.start;
    const defEnd = fy.end;
    setStartDate(defStart);
    setEndDate(defEnd);
    setDateRangeText(fy.displayText);
    setAppliedFilters({
      createdBy: null,
      statusCode: null,
      statusCodesCsv: null,
      businessVerticalCodesCsv: null,
      fromDate: defStart,
      toDate: defEnd,
      raisedByUserIdCode: null,
      searchText: null,
      onlyQueriesRaisedByMe: false,
      onlyQueriesToRespondByMe: false,
      onlySpecialCase: false,
      onlySlaBreached: false,
    });
    setPageNumber(1);

    if (dateRangePickerRef.current) {
      $(dateRangePickerRef.current).val(fy.displayText);
    }
    [
      ".complaint-type-select",
      ".raised-by-select",
    ].forEach((selector) => {
      const $el = $(selector);
      if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
        $el.val("").trigger("change.select2");
      }
    });
  };

  // Initialize daterangepicker (only when filter panel is expanded — input not in DOM when collapsed)
  useEffect(() => {
    if (isFilterCollapsed || !dateRangePickerRef.current) return;

    const timer = setTimeout(() => {
      const $input = $(dateRangePickerRef.current);
      if (!$input.length) return;

    // If dashboard drill-down provided date range, do NOT overwrite it with FY defaults.
    const fromDateParam = urlSearchParams.get("fromDate");
    const toDateParam = urlSearchParams.get("toDate");
    const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
    const fromUrl = fromDateParam ? decodeURIComponent(fromDateParam) : "";
    const toUrl = toDateParam ? decodeURIComponent(toDateParam) : "";
    const hasUrlRange = isIso(fromUrl) || isIso(toUrl);

    const initDateRangePicker = () => {
      // Destroy existing instance if any
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }

      const today = moment();
      const yesterday = moment().subtract(1, "days");
      const thisWeekStart = getWeekStart(new Date());
      const lastWeekStart = moment(thisWeekStart).subtract(7, "days");
      const lastWeekEnd = moment(thisWeekStart).subtract(1, "days");
      const thisMonthStart = moment().startOf("month");
      const lastMonthStart = moment().subtract(1, "month").startOf("month");
      const lastMonthEnd = moment().subtract(1, "month").endOf("month");
      const thisQuarterStart = getQuarterStart(new Date());
      const lastQuarterStart = moment(thisQuarterStart).subtract(3, "months");
      const lastQuarterEnd = moment(thisQuarterStart).subtract(1, "days");
      const thisFYStart = getFinancialYearStart(new Date());
      const thisFYEnd = moment(thisFYStart).add(1, "year").subtract(1, "day"); // 31-Mar of same FY
      const lastFYStart = moment(thisFYStart).subtract(1, "year");
      const lastFYEnd = moment(thisFYStart).subtract(1, "days");

      try {
        $input.daterangepicker({
          autoUpdateInput: false,
          locale: {
            cancelLabel: "Clear",
            applyLabel: "Apply",
            format: "DD-MM-YYYY",
          },
          opens: "left",
          drops: "down",
          showDropdowns: true,
          ranges: {
            Today: [today, today],
            Yesterday: [yesterday, yesterday],
            "This Week": [moment(thisWeekStart), today],
            "Last Week": [lastWeekStart, lastWeekEnd],
            "This Month": [thisMonthStart, today],
            "Last Month": [lastMonthStart, lastMonthEnd],
            "This Quarter": [moment(thisQuarterStart), today],
            "Last Quarter": [lastQuarterStart, lastQuarterEnd],
            "This Financial Year": [moment(thisFYStart), thisFYEnd],
            "Last Financial Year": [lastFYStart, lastFYEnd],
          },
        });

        $input.on("apply.daterangepicker", function (ev, picker) {
          const start = picker.startDate.format("YYYY-MM-DD");
          const end = picker.endDate.format("YYYY-MM-DD");
          const displayText =
            picker.startDate.format("DD-MM-YYYY") +
            " - " +
            picker.endDate.format("DD-MM-YYYY");

          setStartDate(start);
          setEndDate(end);
          setDateRangeText(displayText);
          $(this).val(displayText);
        });

        $input.on("cancel.daterangepicker", function (ev, picker) {
          setStartDate("");
          setEndDate("");
          setDateRangeText("");
          $(this).val("");
        });

        // Default date range: current Financial Year (same as /csrp/approver-dashboard),
        // but do not override dashboard drill-down URL date range.
        if (!hasUrlRange) {
          const fyStart = moment(thisFYStart);
          const fyEnd = moment(thisFYEnd);
          const defaultDisplay =
            fyStart.format("DD-MM-YYYY") + " - " + fyEnd.format("DD-MM-YYYY");
          setStartDate(fyStart.format("YYYY-MM-DD"));
          setEndDate(fyEnd.format("YYYY-MM-DD"));
          setDateRangeText(defaultDisplay);
          $input.val(defaultDisplay);
        }
      } catch (error) {
        console.error("Error initializing daterangepicker:", error);
      }
    };

    // Initialize immediately
    initDateRangePicker();

    const displayText =
      dateRangeText ||
      (startDate && endDate
        ? `${moment(startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(endDate, "YYYY-MM-DD").format("DD-MM-YYYY")}`
        : defaultFY.displayText);
    $input.val(displayText);

    // Reinitialize on click/focus if not working (for collapsed panels)
    const handleInteraction = () => {
      if (!$input.data("daterangepicker")) {
        initDateRangePicker();
      }
    };

    $input.on("click.daterangepicker-reinit", handleInteraction);
    $input.on("focus.daterangepicker-reinit", handleInteraction);
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (dateRangePickerRef.current) {
        const $input = $(dateRangePickerRef.current);
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.off("click.daterangepicker-reinit");
          $input.off("focus.daterangepicker-reinit");
          $input.data("daterangepicker").remove();
        }
      }
    };
  }, [isFilterCollapsed, urlSearchKey, urlSearchParams, dateRangeText, startDate, endDate]);

  // Initialize select2 for other filter dropdowns (re-run when master lists load)
  useEffect(() => {
    if (isFilterCollapsed) return;

    const selectors = [
      { selector: ".complaint-type-select", placeholder: "All SR Categories" },
      {
        selector: ".raised-by-select",
        placeholder: "Raised By",
      },
    ];

    selectors.forEach(({ selector, placeholder }) => {
      const $el = $(selector);
      if ($el.length === 0) return;
      // Select2 over hundreds of Raised By options freezes the page — keep native <select>.
      if (selector === ".raised-by-select" && (userList?.length || 0) > 100) {
        if ($el.hasClass("select2-hidden-accessible")) {
          $el.select2("destroy");
        }
        return;
      }
      if ($el.hasClass("select2-hidden-accessible")) {
        $el.select2("destroy");
      }
      $el.select2({
        placeholder,
        allowClear: true,
        width: "100%",
      });

      // IMPORTANT: Select2 mutates the DOM; bind change handlers directly so React state stays in sync.
      if (selector === ".complaint-type-select") {
        $el.off("change.bcmFilters").on("change.bcmFilters", function (e) {
          setComplaintTypeFilter(e?.target?.value || "");
        });
      }
      if (selector === ".raised-by-select") {
        $el.off("change.bcmFilters").on("change.bcmFilters", function (e) {
          setRaisedByFilter(e?.target?.value || "");
        });
      }
    });

    return () => {
      selectors.forEach(({ selector }) => {
        const $el = $(selector);
        $el.off("change.bcmFilters");
        if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
          $el.select2("destroy");
        }
      });
    };
  }, [isFilterCollapsed, allData.length, userList.length]);

  // Sync select2 values with state (after reset or external changes)
  useEffect(() => {
    const syncValue = (selector, value) => {
      const $el = $(selector);
      if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
        const currentVal = $el.val() || "";
        const newVal = value || "";
        if (currentVal !== newVal) {
          $el.val(newVal).trigger("change.select2");
        }
      }
    };

    syncValue(".complaint-type-select", complaintTypeFilter);
    syncValue(".raised-by-select", raisedByFilter);
  }, [complaintTypeFilter, raisedByFilter]);

  // Handle sidebar toggle - recalculate DataTable columns
  useEffect(() => {
    if (dataTableInstanceRef.current) {
      // Wait for CSS transition to complete (300ms as defined in App.js)
      const timer = setTimeout(() => {
        try {
          dataTableInstanceRef.current.columns.adjust().draw(false);
        } catch (error) {
          console.error("Error adjusting DataTable columns:", error);
        }
      }, 350); // 350ms to ensure transition completes

      return () => clearTimeout(timer);
    }
  }, [sidebarOpen]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          Approver Service Request List
        </Typography>
        {hasUserType("SE") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/raise-complaint")}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Raise SR
          </Button>
        )}
      </Box>

      {/* Filter Panel */}
      <div
        className="card-panel mb-3 filter-panel"
        style={{ paddingBottom: isFilterCollapsed ? "2px" : "15px" }}
      >
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          onClick={() => setIsFilterCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-expanded={!isFilterCollapsed}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsFilterCollapsed((prev) => !prev);
            }
          }}
          style={{ marginBottom: "14px", cursor: "pointer" }}
        >
          <FilterListIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter Service Requests</span>
          {isFilterCollapsed ? (
            <ExpandMoreIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          ) : (
            <ExpandLessIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          )}
        </div>

        {!isFilterCollapsed && (
        <div className="container-fluid">
          <div className="row g-3">
            {/* Status Dropdown (Multiselect) */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <CustomMultiSelect
                options={statusOptions}
                value={statusFilter || []}
                onChange={(selected) =>
                  setStatusFilter(Array.isArray(selected) ? selected : [])
                }
                placeholder="Select Status"
                disabled={false}
              />
            </div>

            {/* SR Category Dropdown */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label">SR Category</label>
              <select
                className="form-select form-control-light filter-select complaint-type-select"
                value={complaintTypeFilter}
                onChange={(e) => setComplaintTypeFilter(e.target.value)}
              >
                <option value="">All SR Categories</option>
                {complaintTypeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Business Vertical Dropdown */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label">Business Vertical</label>
              <CustomMultiSelect
                options={bvOptions}
                value={productVerticalFilter || []}
                onChange={(selected) =>
                  setProductVerticalFilter(Array.isArray(selected) ? selected : [])
                }
                placeholder="Select Business Vertical"
                disabled={false}
              />
            </div>

            {/* Raised By Dropdown - from user master */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label">Raised By</label>
              <select
                className="form-select form-control-light filter-select raised-by-select"
                value={raisedByFilter}
                onChange={(e) => setRaisedByFilter(e.target.value)}
              >
                <option value="">Raised By</option>
                {raisedByOptions.map((opt) => (
                  <option key={opt.id || opt} value={typeof opt === "object" ? (opt.id ?? "") : opt}>
                    {typeof opt === "object" ? (opt.text || opt.id || "") : opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-3 mt-1">
            {/* SR / Invoice Number — hidden per product request */}
            <div className="col-12 col-md-6 col-lg-3 d-none">
              <label className="input-label">SR No. / Invoice No.</label>
              <input
                type="text"
                className="form-control form-control-light"
                placeholder="Enter SR or Invoice No."
                value={srInvoiceFilter}
                list="sr-invoice-suggestions"
                onChange={(e) => setSrInvoiceFilter(e.target.value)}
              />
              <datalist id="sr-invoice-suggestions">
                {srInvoiceSuggestions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>

            {/* Date Range Picker */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Select Date Range</label>
              <div
                className="input-icon filter-daterange-wrapper filter-daterangepickerWrpr"
                style={{ position: "relative" }}
              >
                <input
                  type="text"
                  ref={dateRangePickerRef}
                  className="form-control form-control-light text-start filter-daterangepicker"
                  placeholder="Select date range"
                  readOnly
                  style={{ height: "42px", textAlign: "left" }}
                />
                <CalendarTodayIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="col-12 col-md-12 col-lg-5">
              <label
                className="input-label d-block"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons flex-wrap">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={handleFilter}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <FilterListIcon fontSize="small" className="me-1" />
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={handleReset}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {!listFetchDone && allData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading service requests...</Typography>
            </Box>
          ) : allData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No service requests found for the selected filters.
              </Typography>
            </Box>
          ) : (
            <div
              className="table-responsive bcm-sr-table-wrapper"
              ref={srTableWrapperRef}
            >
              <table
                ref={srTableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default function BCMServiceRequestList() {
  const location = useLocation();
  const { searchParams, paramsReady } = useEncryptedSearchParams();
  const drilldownPlain = location.state?.bcmDrilldownPlain;

  // Dashboard click passes plain params in location.state — use immediately (no ENC wait).
  const effectiveParams = useMemo(() => {
    if (drilldownPlain && typeof drilldownPlain === "object") {
      try {
        return new URLSearchParams(drilldownPlain);
      } catch (_) {
        /* fall through */
      }
    }
    if (searchParams) return searchParams;
    // Never block the page on decrypt — render with empty params; bind/load will catch up.
    return new URLSearchParams();
  }, [drilldownPlain, searchParams]);

  // Ignore paramsReady for rendering — a waiting spinner here looked like the stuck overlay.
  void paramsReady;

  return <BCMServiceRequestListPage urlSearchParams={effectiveParams} />;
}


